import apiClient from 'src/api/axiosClient';
import { CustomAxiosRequestConfig } from 'src/api/axiosClient/types';
import { generateUrlWithCompany } from 'src/helpers/helpers';
import { isTransientFailure } from './apiErrors';
import { SignupAction, SignupFunnelStepPath, SignupState } from '../types';

// A company with no funnel behind it answers 404 here, which must not surface as a toast on every login.
const requestConfig: CustomAxiosRequestConfig = { skipGenericToast: true };

// Where the funnel ends when the application offers a call. Not a funnel step of its own.
const SIGNUP_BOOK_CALL_PATH = '/signup/book-call';

const SIGNUP_FUNNEL_STEP_PATHS: SignupFunnelStepPath[] = [
  '/signup/phone-number',
  '/signup/payment',
];

/**
 * Every funnel step, the three before the account exists included. Nothing outside the funnel may redirect away
 * from these: an unfinished account looks exactly like a company whose onboarding was never touched, so
 * whatever would move one would move a client standing where they belong.
 */
const SIGNUP_STEP_PATHS: string[] = [
  '/signup/account',
  '/signup/verify',
  '/signup/company',
  ...SIGNUP_FUNNEL_STEP_PATHS,
  // Listed although it is past the funnel: the startup check reads the account behind it as one that never
  // finished onboarding, and would move a client off the call they are booking.
  SIGNUP_BOOK_CALL_PATH,
];

export const signupCompletedRoute = (companyId?: string): string =>
  generateUrlWithCompany('/onboarding/completed', companyId);

export const signupBookCallRoute = (companyId?: string): string =>
  generateUrlWithCompany(SIGNUP_BOOK_CALL_PATH, companyId);

export const isSignupStepPath = (pathname: string): boolean =>
  SIGNUP_STEP_PATHS.some(path => pathname === path || pathname.startsWith(`${path}/`));

// Which action opens which screen. More than one where a screen serves several, as the payment step does.
const STEP_ACTIONS: Record<SignupFunnelStepPath, SignupAction[]> = {
  '/signup/phone-number': ['choose_number'],
  // `finish` among them because the payment screen is where the funnel is finished, without a screen of its
  // own: a client who left before it happened comes back here and it happens on arrival.
  '/signup/payment': ['checkout', 'retry_payment', 'await_provisioning', 'finish'],
};

/**
 * Which funnel screens the client may stand on. What is open comes from the API rather than being read out of
 * `status` and `step` here, because a second reading can disagree with the permission being enforced. The
 * order is this file's own: front to back is what makes the last answer the furthest screen open to the client.
 */
export const signupAllowedSteps = (state: SignupState): SignupFunnelStepPath[] =>
  SIGNUP_FUNNEL_STEP_PATHS.filter(
    path => STEP_ACTIONS[path].some(action => state.allowedActions.includes(action)),
  );

// The furthest screen open to them: the ones behind it may be gone back to, but are not places to be put.
export const signupResumeRoute = (state: SignupState): string | null => {
  const allowed = signupAllowedSteps(state);
  return allowed[allowed.length - 1] ?? null;
};

// Where a client with no funnel belongs: the wizard is the only way left to set such an account up.
export const signupOnboardingRoute = (companyId: string): string =>
  generateUrlWithCompany('/onboarding', companyId);

/**
 * Where a client goes when the funnel screen they asked for is not one of theirs. An expired funnel leaves the
 * ordinary wizard as the only way to set the account up.
 */
export const signupStepFallbackRoute = (state: SignupState, companyId: string): string =>
  signupResumeRoute(state)
  ?? (state.status === 'completed'
    ? signupCompletedRoute(companyId)
    : signupOnboardingRoute(companyId));

/**
 * What the funnel behind a company turned out to be. `none` is an answer, `unavailable` the absence of one,
 * and reading the second as the first opens the onboarding wizard beside an unfinished funnel — asking a
 * second time for the card that funnel is already charging.
 */
export type SignupResumeLookup =
  | { kind: 'resume'; route: string }
  | { kind: 'none' }
  | { kind: 'unavailable' };

/**
 * Deliberately not a hook: this is called from `AuthContext` during startup, where the company id is only
 * known part-way through. Most companies never went through the funnel, so a 404 is the ordinary answer and
 * lands in `none`.
 */
export const fetchSignupResumeLookup = async(companyId: string): Promise<SignupResumeLookup> => {
  try {
    const { data } = await apiClient.get<SignupState>(`/signup/companies/${companyId}/state`, requestConfig);
    const route = signupResumeRoute(data);
    return route == null ? { kind: 'none' } : { kind: 'resume', route: route };
  } catch (error) {
    return isTransientFailure(error) ? { kind: 'unavailable' } : { kind: 'none' };
  }
};
